import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, ArrowDown } from "lucide-react";
import { waLink, WA_MESSAGES, trackWhatsApp } from "@/lib/whatsapp";
import { IMAGES } from "@/lib/images";
import WhatsAppIcon from "./WhatsAppIcon";

const EASE = [0.22, 1, 0.36, 1];

const MaskLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const bgScale = useTransform(scrollY, [0, 900], [1.08, 1.28]);

  return (
    <header id="top" className="relative flex min-h-screen items-end overflow-hidden sm:items-center">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={IMAGES.hero}
          alt="Cristo Redentor ao entardecer, Rio de Janeiro"
          className="h-full w-full object-cover"
        />
      </motion.div>

      <div className="absolute inset-0 bg-gradient-to-t from-[#0D1117] via-[#0D1117]/45 to-[#0D1117]/30" />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 70% 35%, rgba(245,158,11,0.14), transparent 70%)",
        }}
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-20 pt-36 sm:px-8 sm:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
          className="mb-8 flex w-fit items-center gap-2 rounded-full border border-amber-400/30 bg-[#0D1117]/60 px-4 py-2 backdrop-blur-md"
        >
          <ShieldCheck className="h-4 w-4 text-amber-400" />
          <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-300">
            Guia Credenciado · Cadastur 19.967376.16-7
          </span>
        </motion.div>

        <h1
          data-testid="hero-title"
          className="max-w-4xl font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-stone-50 sm:text-5xl lg:text-7xl"
        >
          <MaskLine delay={0.35}>Conheça o Rio</MaskLine>
          <MaskLine delay={0.5}>
            <em className="font-accent font-medium italic text-amber-400">sem pressa.</em>
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
          className="mt-7 max-w-xl text-base leading-relaxed text-stone-300 sm:text-lg"
        >
          Experiências guiadas, confortáveis e personalizadas para você conhecer o Rio
          de Janeiro no seu ritmo.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#passeios"
            data-testid="hero-cta-primary"
            className="group flex items-center gap-3 rounded-full bg-amber-400 px-7 py-4 text-sm font-bold uppercase tracking-wider text-[#0D1117] shadow-[0_0_40px_rgba(234,179,8,0.3)] transition-[background-color,transform] duration-300 hover:scale-[1.03] hover:bg-amber-300"
          >
            Ver experiências
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
          </a>
          <a
            href={waLink(WA_MESSAGES.default)}
            onClick={() => trackWhatsApp("hero")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-secondary"
            className="flex items-center gap-2.5 rounded-full bg-[#25D366] px-7 py-4 text-sm font-bold uppercase tracking-wider text-white shadow-[0_0_40px_rgba(37,211,102,0.35)] transition-[background-color,transform] duration-300 hover:scale-[1.03] hover:bg-[#20BD5A]"
          >
            <WhatsAppIcon className="h-4 w-4" />
            Consultar e reservar
          </a>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-14 font-mono text-[11px] uppercase tracking-[0.25em] text-stone-400"
        >
          22.9519° S, 43.2105° W — Corcovado, Rio de Janeiro
        </motion.p>
      </div>
    </header>
  );
}
